import { Typography } from '@bowtie-ins/bowkit';
import { GRAPHITE } from '@bowtie-ins/bowkit/palette';
import { Product } from '@bowtie-web/common';
import Box from '@material-ui/core/Box';
import Link from '@material-ui/core/Link';
import { compact, startCase } from 'lodash';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { EXTRA_PRODUCT_SPECS_BY_CODE } from '../../constants/product';
import { ProductSocialProofSpec } from '../../types';

const isPrefixIncludedInProducts = (prefix: string, products: Product[]) => {
  return products.some(product => product.code.startsWith(prefix));
};

type Props = {
  products: Product[];
  socialProof?: ProductSocialProofSpec;
};

const TermAndConditions: React.FC<Props> = ({ products, socialProof }) => {
  const intl = useIntl();

  // fallbacks
  const LINK_CONFIGS = useMemo(
    () => [
      {
        label: <FormattedMessage id="term-and-conditions.vhis.standard" />,
        url: intl.formatMessage({ id: 'link.vhis.standard' }),
        prefix: 'vhis-standard',
      },
      {
        label: <FormattedMessage id="term-and-conditions.vhis.flexi-regular" />,
        url: intl.formatMessage({ id: 'link.vhis.flexi-regular' }),
        prefix: 'vhis-flexi-regular',
      },
      {
        label: <FormattedMessage id="term-and-conditions.vhis.flexi-plus" />,
        url: intl.formatMessage({ id: 'link.vhis.flexi-plus' }),
        prefix: 'vhis-flexi-plus',
      },
      {
        label: (
          <FormattedMessage id="term-and-conditions.vhis.flexi-premium-ward" />
        ),
        url: intl.formatMessage({ id: 'link.vhis.flexi-premium-ward' }),
        prefix: 'vhis-flexi-premium-ward',
      },
      {
        label: (
          <FormattedMessage id="term-and-conditions.vhis.flexi-premium-semi-private" />
        ),
        url: intl.formatMessage({ id: 'link.vhis.flexi-premium-semi-private' }),
        prefix: 'vhis-flexi-premium-semi-private',
      },
      {
        label: (
          <FormattedMessage id="term-and-conditions.vhis.flexi-premium-private" />
        ),
        url: intl.formatMessage({ id: 'link.vhis.flexi-premium-private' }),
        prefix: 'vhis-flexi-premium-private',
      },
      {
        label: (
          <FormattedMessage id="term-and-conditions.medical.cancer-fighter-100" />
        ),
        url: intl.formatMessage({ id: 'link.medical.cancer-fighter-100' }),
        prefix: 'cancer-fighter-100',
      },
      {
        label: (
          <FormattedMessage id="term-and-conditions.medical.cancer-fighter-200" />
        ),
        url: intl.formatMessage({ id: 'link.medical.cancer-fighter-200' }),
        prefix: 'cancer-fighter-200',
      },
      {
        label: (
          <FormattedMessage id="term-and-conditions.medical.cancer-fighter-300" />
        ),
        url: intl.formatMessage({ id: 'link.medical.cancer-fighter-300' }),
        prefix: 'cancer-fighter-300',
      },
      {
        label: (
          <FormattedMessage id="term-and-conditions.accidental-medical.touch-wood-protector" />
        ),
        url: intl.formatMessage({
          id: 'link.accidental-medical.touch-wood-protector',
        }),
        prefix: 'touch-wood-protector',
      },
      {
        label: <FormattedMessage id="term-and-conditions.life.term-life" />,
        url: intl.formatMessage({ id: 'link.life.term-life' }),
        prefix: 'term-life',
      },
      {
        label: (
          <FormattedMessage id="term-and-conditions.life.term-level-life-5" />
        ),
        url: intl.formatMessage({ id: 'link.life.term-level-life-5' }),
        prefix: 'term-level-life-5',
      },
      {
        label: (
          <FormattedMessage id="term-and-conditions.life.term-level-life-10" />
        ),
        url: intl.formatMessage({ id: 'link.life.term-level-life-10' }),
        prefix: 'term-level-life-10',
      },
      {
        label: (
          <FormattedMessage id="term-and-conditions.life.term-level-life-20" />
        ),
        url: intl.formatMessage({ id: 'link.life.term-level-life-20' }),
        prefix: 'term-level-life-20',
      },
      {
        label: (
          <FormattedMessage id="term-and-conditions.critical-illness.term-ci" />
        ),
        url: intl.formatMessage({ id: 'link.critical-illness.term-ci' }),
        prefix: 'term-ci',
      },
      {
        label: (
          <FormattedMessage id="term-and-conditions.critical-illness.term-ci-plus" />
        ),
        url: intl.formatMessage({ id: 'link.critical-illness.term-ci-plus' }),
        prefix: 'term-ci-plus',
      },
      {
        label: (
          <FormattedMessage id="term-and-conditions.critical-illness.term-ci-max" />
        ),
        url: intl.formatMessage({ id: 'link.critical-illness.term-ci-max' }),
        prefix: 'term-ci-max',
      },
      {
        label: (
          <FormattedMessage id="term-and-conditions.life.last-wish-rider-5" />
        ),
        url: intl.formatMessage({ id: 'link.life.last-wish-rider-5' }),
        prefix: 'last-wish-rider-5',
      },
      {
        label: (
          <FormattedMessage id="term-and-conditions.life.last-wish-rider-10" />
        ),
        url: intl.formatMessage({ id: 'link.life.last-wish-rider-10' }),
        prefix: 'last-wish-rider-10',
      },
      {
        label: (
          <FormattedMessage id="term-and-conditions.life.last-wish-rider-20" />
        ),
        url: intl.formatMessage({ id: 'link.life.last-wish-rider-20' }),
        prefix: 'last-wish-rider-20',
      },
      {
        label: (
          <FormattedMessage id="term-and-conditions.life.last-wish-rider" />
        ),
        url: intl.formatMessage({ id: 'link.life.last-wish-rider' }),
        prefix: 'last-wish-rider',
      },
    ],
    [intl],
  );

  const linksEl = useMemo(() => {
    const configs = compact(
      products.map(product => {
        const { code, documents } = product;
        const label =
          EXTRA_PRODUCT_SPECS_BY_CODE[code]?.premiumScheduleLabel || '';

        // find the latest premium schedule document
        const document = [...(documents || [])]
          .reverse()
          .find(({ type }) => type === 'premium-schedule');

        const premiumScheduleUrl = intl.locale.startsWith('zh')
          ? document?.file_zh
          : document?.file_en;

        if (premiumScheduleUrl) {
          return {
            url: premiumScheduleUrl,
            prefix: '',
            label,
          };
        }
        const [fallbackConfig] = LINK_CONFIGS.filter(config =>
          isPrefixIncludedInProducts(config.prefix, [product]),
        );
        return fallbackConfig;
      }),
    );

    if (configs.length > 0) {
      const formattedMessageId =
        configs.length === 1
          ? 'term-and-conditions.3'
          : 'term-and-conditions.2';
      return (
        <>
          <FormattedMessage
            id={formattedMessageId}
            values={{
              links: () =>
                configs.map((config, ix) => {
                  return (
                    <React.Fragment key={ix}>
                      <Link
                        href={config.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        data-ui-component="Link"
                        data-ui-variant={`${startCase(
                          config.prefix,
                        )} Premium Schedule`}
                      >
                        {config.label}
                      </Link>
                      {ix !== configs.length - 1 && <span> / </span>}
                    </React.Fragment>
                  );
                }),
            }}
          />
        </>
      );
    } else {
      return null;
    }
  }, [intl, products, LINK_CONFIGS]);

  return (
    <Box display="flex" flexDirection="column" gridGap={8}>
      <Typography
        variant="body-s"
        fontWeight="medium"
        style={{ color: GRAPHITE }}
      >
        <FormattedMessage
          id="term-and-conditions.1"
          values={{
            // eslint-disable-next-line react/display-name
            a: msg => (
              <Link
                href={intl.formatMessage({ id: 'link.ia.levy' })}
                target="_blank"
                rel="noreferrer noopener"
              >
                {msg}
              </Link>
            ),
          }}
        />
        {linksEl}
      </Typography>
      {!!socialProof && (
        <Typography
          variant="body-s"
          fontWeight="medium"
          style={{ color: GRAPHITE }}
        >
          <FormattedMessage
            id={`term-and-conditions.social-proof.${socialProof.product_code}`}
            values={{
              percentage: socialProof.comparison_result,
              min_age: socialProof.min_age,
              max_age: socialProof.max_age,
            }}
          />
        </Typography>
      )}
    </Box>
  );
};

export default TermAndConditions;
